import * as z from 'zod';
import type { UseStore } from 'idb-keyval';
import { createStore, get, set } from 'idb-keyval';

let store: UseStore;
function berthPredictionStore(): UseStore {
  if (!store) store = createStore('berthPrediction-cache', 'keyval');
  return store;
}

export const cachedBerthPredictionResultSchema = z.object({
  input: z.object({
    berthName: z.string().nullable().optional(),
    cargo: z.string().nullable().optional(),
    operation: z.enum(['D', 'L']),
    portName: z.string().nullable().optional(),
    vesselName: z.string().nullable().optional(),
    vesselImo: z.number().nullable().optional(),
    totalQuantity: z.number(),
  }),
  mapping: z.record(z.any()),
  prediction: z.object({
    pred: z.number(),
  }),
  cachedAt: z.number(),
  quantityThreshold: z.number(),
});

export type CachedBerthPrediction = z.infer<
  typeof cachedBerthPredictionResultSchema
>;

export type CachedBerthPredictionInput = NonNullable<
  CachedBerthPrediction['input']
>;

export type UseBerthPredictionCacheResult = {
  getCachedPrediction: (
    input: CachedBerthPredictionInput,
  ) => Promise<CachedBerthPrediction | null>;
  savePrediction: (result: CachedBerthPrediction) => Promise<void>;
};

/** Quantities differing by this much (pct) are grouped under the same key*/
export const QUANTITY_GROUPING_THRESHOLD = 10 / 100;

/** "Hashes" the input object into a single string key for storing by.
 * Basically concatenates values with some processing to allow grouping similar inputs. */
const inputToKey = (input: CachedBerthPredictionInput): string => {
  const quantityBin = Math.ceil(
    Math.log(input.totalQuantity ?? 0) /
      Math.log(1 + QUANTITY_GROUPING_THRESHOLD),
  );
  return `${input.portName}-${input.vesselName}-${input.operation}-${input.cargo}-${quantityBin}`;
};

const CACHE_VALID_DURATION = 30 * 24 * 60 * 60 * 1000;

export const getCachedPrediction = async (
  input: CachedBerthPredictionInput,
): Promise<CachedBerthPrediction | null> => {
  try {
    const key = inputToKey(input);
    const action = await get(key, berthPredictionStore());

    if (!action) return null;

    try {
      const parsedValue = cachedBerthPredictionResultSchema.parse(action);
      if (
        parsedValue.cachedAt < Date.now() - CACHE_VALID_DURATION ||
        parsedValue.quantityThreshold !== QUANTITY_GROUPING_THRESHOLD
      ) {
        return null;
      }
      return parsedValue;
    } catch (err) {
      // If we update the schema for storing predictions we will fail the parsing here. This will self correct when the updated value overwrites the old one.
      // This should only happen once for each key. If it happens more often, check for problems with the data.
      console.warn(
        `Error parsing cached prediction result. This is usually fine if the prediction store schema recently updated.`,
        err,
      );
      return null;
    }
  } catch (err) {
    throw err;
  }
};

export const saveCachedPredictions = async (
  result: CachedBerthPrediction,
): Promise<void> => {
  try {
    const key = inputToKey(result.input);
    await set(key, result, berthPredictionStore());
  } catch (err) {
    throw err;
  }
};
